import { useCallback } from 'react';
import {
    usePutLiftOperatorMutation,
    useGetLiftOperatorQuery,
} from '@/api/v2/liftOperator';
import { useNotification } from '@starjack/features/notifications';
import { useAuthentication } from '@/hooks/useAuthentication';
import { LiftOperator } from '@/models/liftoperator';

type UseUpdateLiftOperatorUserProps = {
    onSuccess?: (liftOperator: LiftOperator) => void;
    onError?: (error: unknown) => void;
};

export const useUpdateLiftOperatorUser = ({
    onSuccess,
    onError,
}: UseUpdateLiftOperatorUserProps = {}) => {
    const { liftOperatorData } = useAuthentication();
    const { data: currentLiftOperator } = useGetLiftOperatorQuery({
        liftOperatorId: liftOperatorData?.liftOperatorId,
    });
    const [putLiftOperator, { isLoading }] = usePutLiftOperatorMutation();
    const { addNotification } = useNotification();

    const updateLiftOperatorPassword = useCallback(
        (password: string) => {
            if (!currentLiftOperator) {
                const error = 'Keine Bergbahn-Daten verfügbar';
                addNotification({
                    message: error,
                    type: 'error',
                });
                onError?.(error);
                return Promise.reject(error);
            }

            const updatedLiftOperator: LiftOperator = {
                ...currentLiftOperator,
                password,
            };

            return putLiftOperator(updatedLiftOperator)
                .unwrap()
                .then((savedLiftOperator) => {
                    addNotification({
                        message: 'Passwort erfolgreich aktualisiert',
                        type: 'success',
                    });

                    onSuccess?.(savedLiftOperator);
                    return savedLiftOperator;
                })
                .catch((reason) => {
                    console.error(reason);

                    const message =
                        'Fehler beim Aktualisieren des Passworts. Versuchen Sie es später noch einmal';

                    addNotification({
                        message,
                        type: 'error',
                    });

                    onError?.(reason);
                    throw reason;
                });
        },
        [
            currentLiftOperator,
            putLiftOperator,
            addNotification,
            onSuccess,
            onError,
        ]
    );

    return {
        updateLiftOperatorPassword,
        isLoading,
        isReady: !!currentLiftOperator,
    };
};
